// Copyright (c) 2021 WiseTime. All rights reserved.

import { FieldMask } from "google-protobuf/google/protobuf/field_mask_pb"
import { includes, join, last, take } from "ramda"

// Convert camelCase protobuf field names into snake_case
// The field conversion follows some well-defined rules, so we can reverse the process: 
// https://developers.google.com/protocol-buffers/docs/reference/javascript-generated
export const toProtoSnakeCase = (fieldName: string): string => {
  const trimmedFieldName = fieldName.trim()
  const tokens = trimmedFieldName.split(/(?=[A-Z])/)
  if (tokens.length === 0) {
    throw new Error("malformed tokens received when converting to protobuf snake case")
  }

  // Remove List or Map artifacts
  // For example, a repeated field called "apples" would be converted by protobuf to "applesList"
  const cleanedTokens = includes(last(tokens), ["List", "Map"]) ? take(tokens.length - 1, tokens) : tokens
  const lowercasedTokens = cleanedTokens.map(token => token.toLowerCase())

  return join("_", lowercasedTokens)
}

type FieldMaskBuilder<T> = {
  _paths: string[],

  withField: (field: keyof T, condition?: boolean) => FieldMaskBuilder<T>,
  withNested: <N>(field: keyof T, builder: (builder: FieldMaskBuilder<N>) => void, condition?: boolean) => FieldMaskBuilder<T>,
  build: () => FieldMask,
}

export const fieldMaskFor = <T>(): FieldMaskBuilder<T> => {
  const builder: FieldMaskBuilder<T> = {
    _paths: [],

    // Add the given field to the fieldmask if the condition is true.
    // Not specifying a condition is equivalent to having a true condition.
    withField(field: keyof T, condition?: boolean) {
      if (condition !== undefined && condition === false) {
        return this
      }

      const snakeCaseField = toProtoSnakeCase(field as string)
      if (snakeCaseField.length === 0) {
        throw new Error("empty field received when generating field mask")
      }

      this._paths.push(snakeCaseField)
      return this
    },

    // Add the given nested field to the fieldmask if the condition is true.
    // The method accepts a builder callback that can be used to populate the inner object.
    // Not specifying a condition is equivalent to having a true condition.
    withNested<N>(field: keyof T, builder: (builder: FieldMaskBuilder<N>) => void, condition?: boolean) {
      if (condition !== undefined && condition === false) {
        return this
      }
      
      const snakeCaseField = toProtoSnakeCase(field as string)
      if (snakeCaseField.length === 0) {
        throw new Error("empty field received when generating field mask")
      }

      const maskBuilder = fieldMaskFor<N>()
      builder(maskBuilder)

      this._paths.push(
        ...maskBuilder._paths.map(nestedPath => `${snakeCaseField}.${nestedPath}`)
      )
      
      return this
    },

    build() {
      return new FieldMask().setPathsList(this._paths)
    },
  }

  return builder
}
